import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { parseTheme, type Theme } from "@/model/theme";
import { db, getKv, setKv } from "@/presets/repo";
import { decodeShare } from "@/presets/share";
import { useEditor } from "@/store/editor";

const DRAFT_KEY = "draft";

interface StoredDraft {
  theme: Theme;
  savedId: string | null;
}

/** Writes the current draft right away (used on save and when the page is hidden). */
export function persistDraft(): Promise<void> {
  const { draft, saved } = useEditor.getState();
  return setKv(DRAFT_KEY, { theme: draft, savedId: saved?.id ?? null } satisfies StoredDraft);
}

/**
 * On start: a share link wins, then the autosaved draft, then the default starter.
 * Afterwards the draft is autosaved (debounced) so a reload never loses work.
 */
export function useBoot(): boolean {
  const [ready, setReady] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const shared = location.hash.startsWith("#s=") ? decodeShare(location.hash) : null;
        if (shared) {
          useEditor.getState().open(shared, null);
          useEditor.setState({ past: [] });
          history.replaceState(null, "", location.pathname);
          toast.success(`Opened shared theme “${shared.name}”`, {
            description: "Save it to keep it.",
          });
        } else {
          const stored = await getKv<StoredDraft>(DRAFT_KEY);
          if (stored) {
            const theme = parseTheme(stored.theme);
            const saved = stored.savedId
              ? ((await db.presets.get(stored.savedId))?.theme ?? null)
              : null;
            useEditor.getState().open(theme, saved);
            useEditor.setState({ past: [] });
          }
        }
      } catch (err) {
        toast.error("Could not restore the last session", { description: (err as Error).message });
      } finally {
        setReady(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!ready) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsub = useEditor.subscribe((s, prev) => {
      if (s.draft === prev.draft && s.saved === prev.saved) return;
      clearTimeout(timer);
      timer = setTimeout(() => void persistDraft(), 300);
    });
    const flush = () => {
      if (document.visibilityState === "hidden") {
        clearTimeout(timer);
        void persistDraft();
      }
    };
    document.addEventListener("visibilitychange", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      unsub();
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, [ready]);

  return ready;
}
